import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type {
  Article,
  ArticleCtaRoute,
  ArticleEditorView,
  ArticleRevisionSummary,
  ArticleStatus,
  ArticleType,
  ArticleUpsertInput,
  LocalizedText,
} from "@care-by-ella/contracts";
import { articleUpsertSchema } from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { UnauthorizedError } from "@/lib/clinician-api";
import {
  ArticleApiError,
  createArticle,
  discardArticleDraft,
  fetchArticleEditor,
  fetchArticleRevisions,
  publishArticleDraft,
  rollbackArticle,
  saveArticleDraft,
} from "@/lib/articles-api";
import {
  ARTICLE_CTA_ROUTE_LABELS,
  ARTICLE_STATUS_LABELS,
  ARTICLE_TYPE_LABELS,
  ARTICLE_TYPE_ORDER,
  articleCtaRouteOptions,
  articleErrorMessage,
} from "@/lib/article-labels";
import { MarkdownPreview } from "@/components/MarkdownPreview";
import { RichTextEditor } from "@/components/RichTextEditor";

// TES-207 — create/edit one editable article with a draft→publish flow.
// Mounted at /articles/new (create) and /articles/:key (edit). Editing an
// existing article writes a DRAFT buffer (the live published content keeps
// serving the patient/coach read path untouched); an explicit "Publicera"
// promotes the draft live, bumps the version and snapshots it into the version
// history. A "Versionshistorik" panel lists published versions and offers a
// forward-only "Återställ" that loads an old version back into the draft for
// review. Swedish is the product-approved base, English derived from it — both
// edited side-by-side. Bodies are markdown.

const LOCALES = ["sv", "en"] as const;
const LOCALE_LABEL: Record<(typeof LOCALES)[number], string> = {
  sv: "Svenska",
  en: "Engelska",
};

function emptyLocalized(): LocalizedText {
  return { sv: "", en: "" };
}

function statusChip(status: ArticleStatus) {
  const cls =
    status === "published"
      ? "bg-success/10 text-success border-success/30"
      : "bg-warning/10 text-warning border-warning/30";
  return (
    <span
      className={`inline-flex items-center px-sm py-xs rounded-pill text-xs font-medium border ${cls}`}
    >
      {ARTICLE_STATUS_LABELS[status]}
    </span>
  );
}

function LocalizedInput({
  label,
  value,
  onChange,
  placeholder,
}: {
  label: string;
  value: LocalizedText;
  onChange: (next: LocalizedText) => void;
  placeholder?: string;
}) {
  return (
    <div className="flex flex-col gap-xs">
      <span className="text-xs uppercase tracking-wide text-textSecondary">
        {label}
      </span>
      <div className="grid grid-cols-2 gap-sm">
        {LOCALES.map((locale) => (
          <div key={locale} className="flex flex-col gap-xs">
            <span className="text-[10px] font-medium text-textSecondary">
              {LOCALE_LABEL[locale]}
            </span>
            <input
              value={value[locale]}
              placeholder={placeholder}
              onChange={(e) => onChange({ ...value, [locale]: e.target.value })}
              className="w-full border border-surfaceMuted rounded-md p-sm text-sm focus:outline-none focus:border-brand"
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function MarkdownBody({
  value,
  onChange,
  preview,
}: {
  value: LocalizedText;
  onChange: (next: LocalizedText) => void;
  preview: boolean;
}) {
  return (
    <div className="flex flex-col gap-xs">
      <span className="text-xs uppercase tracking-wide text-textSecondary">
        Brödtext
      </span>
      <div className="grid grid-cols-2 gap-sm">
        {LOCALES.map((locale) => (
          <div key={locale} className="flex flex-col gap-xs">
            <span className="text-[10px] font-medium text-textSecondary">
              {LOCALE_LABEL[locale]}
            </span>
            {preview ? (
              <div className="min-h-[220px] border border-surfaceMuted rounded-md p-sm bg-background">
                <MarkdownPreview source={value[locale]} />
              </div>
            ) : (
              <RichTextEditor
                value={value[locale]}
                onChange={(md) => onChange({ ...value, [locale]: md })}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// Compact version-history list: published versions (newest first) each with a
// forward-only "Återställ" that drafts that version's content for review.
//
// TES-363 — a per-revision "changed fields vs current" hint was considered
// and SKIPPED: the history endpoint returns ArticleRevisionSummary (version/
// changedBy/changedAt only — bodies deliberately excluded to keep the list
// payload small), so there is no content to diff without N extra fetches or a
// DTO change. Revisit if the revisions API grows a detail endpoint.
function VersionHistory({
  revisions,
  liveVersion,
  busy,
  onRollback,
}: {
  revisions: ArticleRevisionSummary[];
  liveVersion: number;
  busy: boolean;
  onRollback: (version: number) => void;
}) {
  if (revisions.length === 0) return null;
  return (
    <section className="flex flex-col gap-sm border-t border-surfaceMuted pt-md">
      <h2 className="text-xs uppercase tracking-wide text-textSecondary">
        Versionshistorik
      </h2>
      <ul className="flex flex-col gap-xs">
        {revisions.map((r) => (
          <li
            key={r.version}
            className="flex items-center justify-between gap-md rounded-md border border-surfaceMuted px-md py-sm text-sm"
          >
            <span className="flex items-center gap-sm">
              <span className="font-medium">v{r.version}</span>
              {r.version === liveVersion ? (
                <span className="text-xs text-success">(aktuell)</span>
              ) : null}
              <span className="text-textSecondary">
                · {r.changedBy ?? "system"} · {r.changedAt.slice(0, 10)}
              </span>
            </span>
            <button
              type="button"
              onClick={() => onRollback(r.version)}
              disabled={busy}
              className="rounded-md border border-surfaceMuted px-md py-xs text-xs text-textSecondary hover:border-brand hover:text-brand transition disabled:opacity-40"
            >
              Återställ
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

export function ArticleEditorRoute() {
  const { key: routeKey } = useParams<{ key: string }>();
  const isCreate = !routeKey;
  const navigate = useNavigate();
  const { signOut } = useAuth();

  const [key, setKey] = useState("");
  const [type, setType] = useState<ArticleType>("mini_lesson");
  const [title, setTitle] = useState<LocalizedText>(emptyLocalized());
  const [body, setBody] = useState<LocalizedText>(emptyLocalized());
  // Optional primary call-to-action button. "" route = inert/non-navigating.
  const [ctaLabel, setCtaLabel] = useState<LocalizedText>(emptyLocalized());
  const [ctaRoute, setCtaRoute] = useState<ArticleCtaRoute | "">("");
  const [meta, setMeta] = useState<Pick<
    Article,
    "status" | "version" | "updatedAt"
  > | null>(null);
  // TES-207 — whether the live row carries a pending draft buffer, how many
  // published versions exist, and the history list.
  const [hasDraft, setHasDraft] = useState(false);
  const [publishedVersions, setPublishedVersions] = useState(0);
  const [revisions, setRevisions] = useState<ArticleRevisionSummary[]>([]);

  const [loading, setLoading] = useState(!isCreate);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false); // publish/discard/rollback in flight
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [preview, setPreview] = useState(false);

  function applyForm(src: {
    type?: ArticleType;
    title: LocalizedText;
    body: LocalizedText;
    cta?: { label: LocalizedText; route?: ArticleCtaRoute };
  }) {
    if (src.type) setType(src.type);
    setTitle(src.title);
    setBody(src.body);
    setCtaLabel(src.cta?.label ?? emptyLocalized());
    setCtaRoute(src.cta?.route ?? "");
  }

  // Hydrate from the editor view: the form shows the DRAFT buffer if present,
  // otherwise the live published content.
  function applyEditorView(view: ArticleEditorView) {
    const a = view.article;
    setKey(a.key);
    setType(a.type);
    applyForm(view.draft ? { ...view.draft, type: a.type } : a);
    setMeta({ status: a.status, version: a.version, updatedAt: a.updatedAt });
    setHasDraft(view.draft != null);
    setPublishedVersions(view.publishedVersions);
  }

  function handleError(e: unknown) {
    if (e instanceof UnauthorizedError) {
      void signOut();
      return;
    }
    if (e instanceof ArticleApiError) {
      setError(articleErrorMessage(e.code));
      return;
    }
    setError(e instanceof Error ? e.message : String(e));
  }

  function loadRevisions(k: string) {
    fetchArticleRevisions(k).then(setRevisions).catch(handleError);
  }

  useEffect(() => {
    if (isCreate) return;
    setLoading(true);
    setError(null);
    fetchArticleEditor(routeKey)
      .then((view) => {
        applyEditorView(view);
        loadRevisions(routeKey);
      })
      .catch(handleError)
      .finally(() => setLoading(false));
    // Re-run only when the route key changes. (react-hooks/exhaustive-deps is
    // not enabled in apps/admin — see .eslintrc.cjs — so no disable directive.)
  }, [routeKey]);

  const keyValid = useMemo(
    () => articleUpsertSchema.shape.key.safeParse(key).success,
    [key],
  );
  const titled = title.sv.trim().length > 0;
  const canSave = keyValid && titled && !saving && !busy;
  // Publishing is possible when there's a pending draft to promote, or the
  // article has never been published (first publication of a created draft).
  const canPublish =
    !isCreate && (hasDraft || meta?.status === "draft") && !saving && !busy;

  function buildInput(): ArticleUpsertInput {
    // A CTA exists only when it has a label (either locale). An empty label
    // means "no CTA" regardless of the route selection.
    const hasCta =
      ctaLabel.sv.trim().length > 0 || ctaLabel.en.trim().length > 0;
    return {
      key: key.trim(),
      type,
      title,
      body,
      ...(hasCta
        ? { cta: { label: ctaLabel, ...(ctaRoute ? { route: ctaRoute } : {}) } }
        : {}),
    };
  }

  // Create mode: create the article row (status draft, v1) then land on its
  // canonical edit URL where the draft→publish flow takes over.
  function createDraftArticle() {
    setSaving(true);
    setError(null);
    setNotice(null);
    createArticle(buildInput())
      .then((a) => {
        navigate(`/articles/${encodeURIComponent(a.key)}`, { replace: true });
      })
      .catch(handleError)
      .finally(() => setSaving(false));
  }

  // Edit mode: persist the current form as the pending draft buffer.
  function saveDraft() {
    if (isCreate) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    saveArticleDraft(routeKey, buildInput())
      .then((view) => {
        applyEditorView(view);
        setNotice("Utkast sparat.");
      })
      .catch(handleError)
      .finally(() => setSaving(false));
  }

  // Promote the saved draft live: bumps version + snapshots a revision.
  function publish() {
    if (isCreate) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    publishArticleDraft(routeKey)
      .then((a) => {
        setMeta({
          status: a.status,
          version: a.version,
          updatedAt: a.updatedAt,
        });
        applyForm(a);
        setHasDraft(false);
        setNotice(`Publicerad v${a.version}.`);
        loadRevisions(routeKey);
        setPublishedVersions((n) => n + 1);
      })
      .catch(handleError)
      .finally(() => setBusy(false));
  }

  function discard() {
    if (isCreate) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    discardArticleDraft(routeKey)
      .then((view) => {
        applyEditorView(view);
        setNotice("Utkast borttaget.");
      })
      .catch(handleError)
      .finally(() => setBusy(false));
  }

  function rollback(version: number) {
    if (isCreate) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    rollbackArticle(routeKey, version)
      .then((view) => {
        applyEditorView(view);
        setNotice(`v${version} laddad som utkast — granska och publicera.`);
      })
      .catch(handleError)
      .finally(() => setBusy(false));
  }

  if (loading) {
    return <div className="text-textSecondary text-sm">Hämtar…</div>;
  }

  return (
    <div className="flex flex-col gap-lg max-w-4xl">
      <div className="flex items-start justify-between gap-md">
        <div className="flex flex-col gap-xs">
          <button
            type="button"
            onClick={() => navigate("/articles")}
            className="text-sm text-brand hover:underline self-start"
          >
            ← Tillbaka till innehåll
          </button>
          <h1 className="font-display text-3xl tracking-tight">
            {isCreate ? "Ny artikel" : title.sv || title.en || key}
          </h1>
          {meta ? (
            <div className="flex items-center gap-sm text-sm text-textSecondary">
              {statusChip(meta.status)}
              <span>version {meta.version}</span>
              {hasDraft ? (
                <span className="text-warning">· utkast väntar</span>
              ) : null}
              <span>· ändrad {meta.updatedAt.slice(0, 10)}</span>
            </div>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => setPreview((p) => !p)}
          className="rounded-md border border-surfaceMuted px-md py-sm text-sm text-textSecondary hover:text-brand hover:border-brand transition"
        >
          {preview ? "Redigera" : "Förhandsgranska"}
        </button>
      </div>

      {error ? (
        <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-md text-sm">
          {error}
        </div>
      ) : null}
      {notice ? (
        <div className="bg-success/10 border border-success/30 text-success rounded-md p-md text-sm">
          {notice}
        </div>
      ) : null}

      <div className="grid grid-cols-2 gap-md">
        <label className="flex flex-col gap-xs">
          <span className="text-xs uppercase tracking-wide text-textSecondary">
            Nyckel
          </span>
          <input
            value={key}
            disabled={!isCreate}
            onChange={(e) => setKey(e.target.value)}
            placeholder="t.ex. blood_test.why_tests"
            className="w-full border border-surfaceMuted rounded-md p-sm text-sm font-mono focus:outline-none focus:border-brand disabled:bg-surfaceMuted/40 disabled:text-textSecondary"
          />
          {isCreate && key.length > 0 && !keyValid ? (
            <span className="text-xs text-danger">
              Endast gemener, siffror och _ , punktseparerade (a-z0-9_.).
            </span>
          ) : (
            <span className="text-xs text-textSecondary">
              {isCreate
                ? "Punktseparerad nyckel, kan inte ändras efteråt."
                : "Nyckeln kan inte ändras."}
            </span>
          )}
        </label>

        <label className="flex flex-col gap-xs">
          <span className="text-xs uppercase tracking-wide text-textSecondary">
            Typ
          </span>
          <select
            value={type}
            onChange={(e) => setType(e.target.value as ArticleType)}
            className="w-full border border-surfaceMuted rounded-md p-sm text-sm bg-surface focus:outline-none focus:border-brand"
          >
            {ARTICLE_TYPE_ORDER.map((t) => (
              <option key={t} value={t}>
                {ARTICLE_TYPE_LABELS[t]}
              </option>
            ))}
          </select>
        </label>
      </div>

      <LocalizedInput
        label="Titel"
        value={title}
        onChange={setTitle}
        placeholder="Titel"
      />

      <MarkdownBody value={body} onChange={setBody} preview={preview} />

      <fieldset className="flex flex-col gap-md border-t border-surfaceMuted pt-md">
        <legend className="text-xs uppercase tracking-wide text-textSecondary">
          Knapp (frivillig)
        </legend>
        <LocalizedInput
          label="Knapptext"
          value={ctaLabel}
          onChange={setCtaLabel}
          placeholder="t.ex. Hitta labb nära mig"
        />
        <label className="flex max-w-sm flex-col gap-xs">
          <span className="text-xs uppercase tracking-wide text-textSecondary">
            Destination
          </span>
          <select
            value={ctaRoute}
            onChange={(e) =>
              setCtaRoute(e.target.value as ArticleCtaRoute | "")
            }
            className="w-full border border-surfaceMuted rounded-md p-sm text-sm bg-surface focus:outline-none focus:border-brand"
          >
            <option value="">Ingen destination (inaktiv knapp)</option>
            {articleCtaRouteOptions(ctaRoute).map((route) => (
              <option key={route} value={route}>
                {ARTICLE_CTA_ROUTE_LABELS[route]}
              </option>
            ))}
          </select>
          <span className="text-xs text-textSecondary">
            Lämna knapptexten tom för att ta bort knappen. Utan destination
            visas knappen men leder ingenstans.
          </span>
        </label>
      </fieldset>

      <div className="flex flex-wrap items-center gap-md border-t border-surfaceMuted pt-md">
        {isCreate ? (
          <button
            type="button"
            onClick={createDraftArticle}
            disabled={!canSave}
            className="rounded-md bg-brand text-white px-lg py-sm text-sm font-medium hover:bg-brand/90 transition disabled:opacity-40"
          >
            {saving ? "Sparar…" : "Skapa utkast"}
          </button>
        ) : (
          <>
            <button
              type="button"
              onClick={saveDraft}
              disabled={!canSave}
              className="rounded-md bg-brand text-white px-lg py-sm text-sm font-medium hover:bg-brand/90 transition disabled:opacity-40"
            >
              {saving ? "Sparar…" : "Spara utkast"}
            </button>
            <button
              type="button"
              onClick={publish}
              disabled={!canPublish}
              className="rounded-md border border-success text-success px-lg py-sm text-sm font-medium hover:bg-success/10 transition disabled:opacity-40"
            >
              {busy ? "…" : "Publicera"}
            </button>
            {hasDraft ? (
              <button
                type="button"
                onClick={discard}
                disabled={saving || busy}
                className="rounded-md border border-surfaceMuted text-textSecondary px-lg py-sm text-sm font-medium hover:border-warning hover:text-warning transition disabled:opacity-40"
              >
                Förkasta utkast
              </button>
            ) : null}
          </>
        )}

        {!canSave && isCreate ? (
          <span className="text-xs text-textSecondary">
            Ange en giltig nyckel och en titel (svenska) för att skapa.
          </span>
        ) : null}
      </div>

      {!isCreate && publishedVersions > 0 ? (
        <VersionHistory
          revisions={revisions}
          liveVersion={meta?.version ?? 0}
          busy={saving || busy}
          onRollback={rollback}
        />
      ) : null}
    </div>
  );
}
